import './App.css';
import Header from "./Header.js";
import watershed from "./images/watershed-cover.jpg";
import { Routes, Route } from "react-router-dom";
import { Card, LightItalicText } from './Style';
import Iframe from 'react-iframe';
import History from './History';
import WatershedPressKit from './Watershed-PressKit';
import Carousel from './Carousel';
import styled from 'styled-components';

const SectionHeader = styled.h2`
  font-family: "kepler-std-display", serif;
  font-weight: 700;
  font-style: italic;
  font-size: 1.8rem;
  letter-spacing: 0.02em;
  color: var(--text-primary);
  text-align: center;

  @media (min-width: 768px) {
    font-size: 2.2rem;
  }
`;

const ProjectTitle = styled.h3`
  font-family: "kepler-std-display", serif;
  font-weight: 700;
  font-style: italic;
  font-size: 1.4rem;
  letter-spacing: 0.02em;
  color: var(--text-primary);
  margin: 0;
`;

const ProjectSection = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  margin-top: 1.5rem;
  gap: 1.5rem;
  max-width: 900px;
  margin-left: auto;
  margin-right: auto;
  padding: 0 1rem;

  @media (min-width: 768px) {
    flex-direction: row;
    align-items: flex-start;
    gap: 2rem;
  }
`;

const ProjectMedia = styled.div`
  width: 100%;
  max-width: 450px;

  @media (min-width: 768px) {
    flex: 1;
    max-width: none;
  }
`;

const ProjectDetails = styled.div`
  width: 100%;
  max-width: 450px;
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;

  @media (min-width: 768px) {
    flex: 1;
    max-width: none;
    align-items: flex-start;
    text-align: left;
  }
`;

function App() {
  return (
    <div className="App">
      <Header />
      <Routes>
        <Route path="/" element={<Home />} />
        <Route path="/watershed-presskit" element={<WatershedPressKit />} />
        <Route path="/history" element={<History />} />
      </Routes>
    </div>
  );
}

function SectionTitle({ children }) {
  return (
    <div style={{
      margin: '3rem auto 1.5rem',
      maxWidth: '450px',
      padding: '0 2rem',
      borderTop: '1px solid var(--border)',
      paddingTop: '3rem'
    }}>
      <SectionHeader>{children}</SectionHeader>
    </div>
  );
}

const BioSection = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1.5rem;
  max-width: 800px;
  margin: 0 auto;
  padding: 2rem 1.5rem;

  @media (min-width: 768px) {
    flex-direction: row;
    align-items: center;
    gap: 2rem;
  }
`;

const BioImage = styled.img`
  width: 280px;
  height: auto;
  border-radius: 8px;
  object-fit: cover;
  flex-shrink: 0;
`;

const BioText = styled.p`
  color: var(--text-secondary);
  line-height: 1.8;
  font-size: 1rem;
  text-align: center;
  margin: 0;

  @media (min-width: 768px) {
    text-align: left;
  }
`;

function Home() {
  return (
    <div style={{ paddingBottom: '4rem' }}>

      {/* BIO */}
      <BioSection>
        <BioImage src="/images/ben-kamen-bio.jpg" alt="Ben Kamen" />
        <BioText>
          Ben Kamen is a musician working in sound installations, generative systems, electronic instruments, songforms, and recorded media. He develops audio apps through Olympia Noise Co, releases music under his own name and as "vivid river," and teaches at The Evergreen State College. Ben lives in the pacific northwest of the US, on land stewarded since time immemorial by the Steh-Chass Band of Indigenous People of the Squaxin Island Tribe.
        </BioText>
      </BioSection>

      {/* INSTALLATIONS */}
      <SectionTitle>installations</SectionTitle>

      <ProjectSection>
        <ProjectMedia>
          <Card>
            <Carousel images={[
              "/images/deschutes-poster.png",
              "/images/deschutes-1.jpeg",
              "/images/deschutes-2.jpeg",
              "/images/deschutes-3.jpeg",
              "/images/deschutes-4.jpeg"
            ]} />
          </Card>
        </ProjectMedia>

        <ProjectDetails>
          <ProjectTitle>deschutes listening room</ProjectTitle>
          <LightItalicText style={{ marginTop: '0.25rem' }}>2025 · Limber Arts, Olympia WA</LightItalicText>
          <p style={{ marginTop: '1rem', color: 'var(--text-secondary)', lineHeight: '1.7' }}>
            A 4-channel surround sound installation documenting and re-imagining the Deschutes River watershed through field recordings. Sounds are organized into water, earth, and fields, combined and layered into an immersive collage of natural and surreal auditory images. The piece generates in real-time, creating an ever-changing sonic environment.
          </p>
          <a href="https://benkamen.substack.com/p/deschutes-listening-room" target="_blank" rel="noreferrer" style={{ marginTop: '0.5rem' }}>
            more...
          </a>
          <div style={{ marginTop: '1rem', width: '100%' }}>
            <Iframe
              src="https://bandcamp.com/EmbeddedPlayer/album=2776258557/size=large/bgcol=0d0d0d/linkcol=5fb4c9/tracklist=false/artwork=small/transparent=true/"
              width="100%"
              height="120px"
              frameBorder={0}
              allow="autoplay; clipboard-write; encrypted-media; fullscreen; picture-in-picture"
            />
          </div>
        </ProjectDetails>
      </ProjectSection>

      <ProjectSection style={{ marginTop: '3rem' }}>
        <ProjectMedia>
          <Card>
            <Carousel
              autoCycle={false}
              media={[
                { type: 'video', src: '/images/instruments/video-1.mp4' },
                { type: 'image', src: '/images/instruments/poster-hq.png' },
                { type: 'image', src: '/images/instruments/amp-randall.png' },
                { type: 'image', src: '/images/instruments/amp-phonograph.png' },
                { type: 'image', src: '/images/instruments/amp-custom.png' },
                { type: 'image', src: '/images/instruments/guitars.jpeg' },
                { type: 'image', src: '/images/instruments/studio-setup.jpeg' }
              ]}
            />
          </Card>
        </ProjectMedia>

        <ProjectDetails>
          <ProjectTitle>instruments</ProjectTitle>
          <LightItalicText style={{ marginTop: '0.25rem' }}>2014 · Dub Narcotic Studio / K Records, Olympia WA</LightItalicText>
          <p style={{ marginTop: '1rem', color: 'var(--text-secondary)', lineHeight: '1.7' }}>
            Originally a site-specific 8-channel sound installation for "Dub Narcotic," the headquarters of K Records. The source material is entirely comprised of individual notes played on instruments residing in the studio: each electric guitar, snare drum, and bass drum. Max/MSP generates playback instructions in real-time, stringing together notes using generative algorithms and manipulating playback speed, attack, and speaker placement. The piece has no beginning or end, cycling stochastically through overlapping algorithms to create endless variations on an immersive and meditative piece that is equal parts music, sound collage, and deep listeining exercise.
          </p>
          <a href="/images/instruments/instruments-slideshow.pdf" target="_blank" rel="noreferrer" style={{ marginTop: '0.5rem' }}>
            more...
          </a>
        </ProjectDetails>
      </ProjectSection>

      <ProjectSection style={{ marginTop: '3rem' }}>
        <ProjectMedia>
          <div style={{ padding: '75.4% 0 0 0', position: 'relative', borderRadius: '4px', overflow: 'hidden' }}>
            <iframe
              src="https://player.vimeo.com/video/1948470?badge=0&autopause=0&player_id=0&app_id=58479"
              frameBorder="0"
              allow="autoplay; fullscreen; picture-in-picture; clipboard-write; encrypted-media"
              referrerPolicy="strict-origin-when-cross-origin"
              style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%' }}
              title="Gravity Music for Five Violins, Viola, and Real or Imaginary 'Cello"
            />
          </div>
        </ProjectMedia>

        <ProjectDetails>
          <ProjectTitle>gravity music</ProjectTitle>
          <LightItalicText style={{ marginTop: '0.25rem' }}>2007 · for Five Violins, Viola, and Real or Imaginary 'Cello</LightItalicText>
          <p style={{ marginTop: '1rem', color: 'var(--text-secondary)', lineHeight: '1.7' }}>
            A musical installation for string ensemble, exploring the infinite world of the harmonic series and just intonation. Performers divide a space into seven concentric rings and freely wander while playing following a map of instructions specifc to each player.  The piece has no fixed beginning or end.  Originally performed at the Washington State Capitol building in 2007.
          </p>
          <a href="/images/gravity-music-score.pdf" target="_blank" rel="noreferrer" style={{ marginTop: '0.5rem' }}>
            score...
          </a>
        </ProjectDetails>
      </ProjectSection>

      {/* MUSIC */}
      <SectionTitle>music</SectionTitle>

      <ProjectSection>
        <ProjectMedia>
          <Card>
            <img src={watershed} alt="ben kamen - watershed album artwork" width="100%" style={{ display: 'block' }} />
          </Card>
          <Card style={{ marginTop: '1rem' }}>
            <Iframe
              src='https://bandcamp.com/EmbeddedPlayer/album=595682112/size=large/bgcol=0d0d0d/linkcol=5fb4c9/tracklist=false/artwork=small/track=2007053731/transparent=true/'
              width="100%"
              height="120px"
              frameBorder={0}
              allow="autoplay; clipboard-write; encrypted-media; fullscreen; picture-in-picture"
            />
          </Card>
          <Card style={{ marginTop: '1rem' }}>
            <Iframe
              src='https://embed.music.apple.com/us/album/watershed/1622910317?app=music&theme=dark'
              width="100%"
              height="175px"
              frameBorder={0}
              allow="autoplay *; encrypted-media *;"
            />
          </Card>
        </ProjectMedia>

        <ProjectDetails>
          <ProjectTitle>watershed</ProjectTitle>
          <LightItalicText style={{ marginTop: '0.25rem' }}>2022</LightItalicText>
          <p style={{ marginTop: '1rem', color: 'var(--text-secondary)', lineHeight: '1.7' }}>
            A record of lush, textured songs informed by daily walks through a temperate rainforest: old growth trees, ferns, and communities of fungus. Oscillator saxophones, pitch-shifted and layered into dense, mossy textures. Banjo strummed through delays and reverbs to create haunted chords.
          </p>
          <p style={{ marginTop: '1rem', color: 'var(--text-secondary)', lineHeight: '1.7' }}>
            <em>Watershed</em> negotiates with the death, decay, and beauty that exists within nature and in human life. A memory device, a collage of fixed moments.
          </p>
          <div style={{ marginTop: '1rem', display: 'flex', gap: '1rem', flexWrap: 'wrap' }}>
            <a href="https://benkamen.bandcamp.com/album/watershed" target="_blank" rel="noreferrer">Bandcamp</a>
            <a href="https://music.apple.com/us/album/watershed/1622910317" target="_blank" rel="noreferrer">Apple Music</a>
          </div>

          <div style={{ marginTop: '1.5rem', width: '100%' }}>
            <LightItalicText style={{ marginBottom: '0.5rem' }}>Purple Shadows</LightItalicText>
            <div style={{ position: 'relative', paddingBottom: '56.25%', height: 0, overflow: 'hidden', borderRadius: '8px' }}>
              <iframe
                src='https://www.youtube.com/embed/HlUSTDT01Cw'
                style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', border: 'none' }}
                allow="autoplay; clipboard-write; encrypted-media; fullscreen; picture-in-picture"
                allowFullScreen
                title="Purple Shadows"
              />
            </div>
          </div>

          <div style={{ marginTop: '1.5rem', width: '100%' }}>
            <LightItalicText style={{ marginBottom: '0.5rem' }}>Make Believe</LightItalicText>
            <div style={{ position: 'relative', paddingBottom: '56.25%', height: 0, overflow: 'hidden', borderRadius: '8px' }}>
              <iframe
                src='https://www.youtube.com/embed/kQr6N2O_RBA'
                style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', border: 'none' }}
                allow="autoplay; clipboard-write; encrypted-media; fullscreen; picture-in-picture"
                allowFullScreen
                title="Make Believe"
              />
            </div>
          </div>
        </ProjectDetails>
      </ProjectSection>

      {/* APPS */}
      <SectionTitle>apps</SectionTitle>

      <ProjectSection>
        <ProjectMedia style={{ maxWidth: '150px' }}>
          <Card>
            <img src="/images/patterning-icon.png" alt="Patterning 3 app icon" width="100%" style={{ display: 'block' }} />
          </Card>
        </ProjectMedia>

        <ProjectDetails>
          <ProjectTitle>patterning 3</ProjectTitle>
          <LightItalicText style={{ marginTop: '0.25rem' }}>drum machine for iOS & macOS</LightItalicText>
          <p style={{ marginTop: '1rem', color: 'var(--text-secondary)', lineHeight: '1.7' }}>
            An award-winning circular drum machine for creating beats through an intuitive, visual interface.  The third installment of Apple's Runner-Up for iPad App of the Year in 2015.  
          </p>
          <a href="https://www.olympianoiseco.com/apps/patterning" target="_blank" rel="noreferrer" style={{ marginTop: '0.5rem' }}>
            more...
          </a>
        </ProjectDetails>
      </ProjectSection>

      <ProjectSection style={{ marginTop: '3rem' }}>
        <ProjectMedia style={{ maxWidth: '150px' }}>
          <Card>
            <img src="/images/ribbons-icon.png" alt="Ribbons app icon" width="100%" style={{ display: 'block' }} />
          </Card>
        </ProjectMedia>

        <ProjectDetails>
          <ProjectTitle>ribbons</ProjectTitle>
          <LightItalicText style={{ marginTop: '0.25rem' }}>instrument for iOS</LightItalicText>
          <p style={{ marginTop: '1rem', color: 'var(--text-secondary)', lineHeight: '1.7' }}>
            An expressive electronic instrument inspired by the Ondes Martenot and Theremin. Glide between notes with continuous pitch sliding, morphing wavetable oscillators, and vertical movement mapped to synthesis parameters. Named 2014 Best iOS Instrument by the Sonic Joy Awards.
          </p>
          <a href="https://www.olympianoiseco.com/apps/ribbons" target="_blank" rel="noreferrer" style={{ marginTop: '0.5rem' }}>
            more...
          </a>
        </ProjectDetails>
      </ProjectSection>

      <ProjectSection style={{ marginTop: '3rem' }}>
        <ProjectMedia style={{ maxWidth: '150px' }}>
          <Card>
            <img src="/images/chordion-icon.png" alt="Chordion app icon" width="100%" style={{ display: 'block' }} />
          </Card>
        </ProjectMedia>

        <ProjectDetails>
          <ProjectTitle>chordion</ProjectTitle>
          <LightItalicText style={{ marginTop: '0.25rem' }}>instrument for iOS</LightItalicText>
          <p style={{ marginTop: '1rem', color: 'var(--text-secondary)', lineHeight: '1.7' }}>
            Play chords with one hand while composing melodies with the other. App Store Best of 2013 and Editor's Choice in 23 countries.
          </p>
          <a href="https://www.olympianoiseco.com/apps/chordion" target="_blank" rel="noreferrer" style={{ marginTop: '0.5rem' }}>
            more...
          </a>
        </ProjectDetails>
      </ProjectSection>

      <Card style={{ marginTop: '2rem', background: 'transparent', border: 'none' }}>
        <a href="https://www.olympianoiseco.com" target="_blank" rel="noreferrer">
          olympianoiseco.com
        </a>
      </Card>

    </div>
  );
}

export default App;
